import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { AnyObject } from 'types/AnyObject';
import { Button, cnMixSpace, HeaderSearchBar, IconClose, useDebounce } from 'uikit';

import type { ColDef, ColGroupDef, IDatasource, RowClassRules } from 'ag-grid-community';
import { useEffect, useState } from 'react';

import './AgreementsAddTable.css';

interface IAgreementsAddTableProps<T> {
    titleModal: string;
    handleCloseAddAgreements: () => void;
    handleAddAgreements: () => void;
    searchPlaceholder?: string;
    searchString?: string;
    handleChange?: ({ value }: any) => void;
    handleSearch?: ({ value }: any) => void;
    handleClearSearch?: () => void;
    tableColumns: (ColDef<T> | ColGroupDef<T>)[];
    isSubmittedAddMtr: boolean;
    addAgreementsStatus: RequestStatus;
    addAgreementsData: AnyObject;
    addAgreementsError: AnyObject[];
    handleCloseErrorModalAdd: () => void;
    handleCreateReportAdd: () => void;
    titleAddError: string;
    isSubmittedDataTable: boolean;
    agreementsStatus: RequestStatus;
    agreementsData: AnyObject | undefined | null;
    agreementsErrors: AnyObject[];
    handleCloseErrorAgreementTable: () => void;
    dataSource?: IDatasource;
    isConfirmDisabled?: boolean;
    isShowSearch?: boolean;
    rowClassRules?: RowClassRules;
}

export const AgreementsAddTable = <T = any,>({
    titleModal,
    handleCloseAddAgreements,
    handleAddAgreements,
    searchPlaceholder,
    searchString,
    handleChange,
    handleSearch,
    handleClearSearch,
    tableColumns,
    dataSource,
    isSubmittedAddMtr,
    addAgreementsStatus,
    addAgreementsData,
    addAgreementsError,
    handleCloseErrorModalAdd,
    handleCreateReportAdd,
    titleAddError,
    isSubmittedDataTable,
    agreementsStatus,
    agreementsData,
    agreementsErrors,
    rowClassRules,
    handleCloseErrorAgreementTable,
    isConfirmDisabled,
    isShowSearch = true,
}: IAgreementsAddTableProps<T>) => {
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string | undefined>(undefined);

    const debouncedChange = useDebounce((value: string | undefined) => {
        setDebouncedSearchTerm(value);
    }, 500);

    useEffect(() => {
        debouncedChange(searchString);
    }, [debouncedChange, searchString]);

    return (
        <ModalDialog
            title={titleModal}
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={handleCloseAddAgreements}
            onConfirm={handleAddAgreements}
            btnConfirmLabel="Добавить"
            btnCancelLabel="Отменить"
            isCommit={addAgreementsStatus === RequestStatus.FETCHING}
            style={{ overflow: 'hidden', width: '1330px' }}
            isOnClickOutsideDisable
            btnConfirmDisabled={isConfirmDisabled}
        >
            <div className={isShowSearch ? '' : 'notShowSearchBar'} id="AgreementsAddTable">
                {isShowSearch ? (
                    <div className={cnMixSpace({ mT: 'xl', mB: 'm' })} style={{ position: 'relative' }}>
                        <HeaderSearchBar
                            className="AddMtrSearch"
                            placeholder={searchPlaceholder}
                            label="поиск"
                            value={searchString}
                            onChange={handleChange}
                            onSearch={handleSearch}
                        />
                        {searchString && (
                            <Button
                                label="Очистить"
                                view="ghost"
                                iconLeft={IconClose}
                                form="round"
                                size="xs"
                                onlyIcon
                                onClick={handleClearSearch}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '16px',
                                    transform: 'translateY(-50%)',
                                    zIndex: 1,
                                }}
                            />
                        )}
                    </div>
                ) : null}

                <div style={{ height: 'calc(50vh - 80px)' }}>
                    {dataSource ? (
                        <AgGridTable
                            key={debouncedSearchTerm}
                            datasource={dataSource}
                            columnDefs={tableColumns}
                            maxConcurrentDatasourceRequests={1}
                            maxBlocksInCache={50}
                            cacheBlockSize={50}
                            rowModelType="infinite"
                            rowClassRules={rowClassRules}
                        />
                    ) : (
                        <AgGridTable
                            key={debouncedSearchTerm}
                            rowData={agreementsData?.items}
                            columnDefs={tableColumns}
                            rowClassRules={rowClassRules}
                        />
                    )}
                </div>
            </div>
            <ErrorHandlingModal
                titleText={titleModal}
                isSubmitted={isSubmittedAddMtr}
                statusRequest={addAgreementsStatus}
                dataRequest={addAgreementsData}
                errorsRequest={addAgreementsError}
                handleCloseModalError={handleCloseErrorModalAdd}
                handleCreateReport={handleCreateReportAdd}
            />
            <ErrorHandlingModal
                titleText={titleAddError}
                isSubmitted={isSubmittedDataTable}
                statusRequest={agreementsStatus}
                dataRequest={agreementsData}
                errorsRequest={agreementsErrors}
                handleCloseModalError={handleCloseErrorAgreementTable}
            />
        </ModalDialog>
    );
};

AgreementsAddTable.displayName = 'AgreementsAddTable';
